// Schema discovery tools for sub-agents.
//
// The full analytics schema is large enough that pasting it into every
// sub-agent's system prompt is wasteful and starts to crowd out the
// child's actual task. These tools let a child grep/navigate the
// schema on demand the way you'd grep a checked-in schema file:
//
//   schema_list_entities()        — orient: what entities exist?
//   schema_describe_entity(name)  — drill in: fields, edges, examples
//   schema_search(query)          — fuzzy lookup across everything
//
// The schema fetch is memoized in analyticsClient.js so all three
// tools share one cache. Failure surfaces an inline error result —
// the agent loop continues so the child can adapt rather than crash.

import { Type } from "@earendil-works/pi-ai";
import { describeSchema } from "./analyticsClient.js";
import { onAuthIdentityChange } from "./authTokenStore.js";

const MAX_RESULTS = 12;

// Fetch the schema's example queries. The schema's examples are
// authoritative for spec shape — they're the contract the backend
// actually accepts. We try every key name we've seen schemas use,
// then fall back to walking the JSON for objects that LOOK like
// query specs (have a `root` key) so we still surface them even if
// the schema uses an unexpected wrapper. Returns an array of example
// objects (possibly empty). Memoized for the page session.
let _examplesPromise = null;
onAuthIdentityChange(() => { _examplesPromise = null; });
export function getSchemaExamples() {
    if (_examplesPromise) return _examplesPromise;
    _examplesPromise = describeSchema()
        .then((schema) => extractExamples(schema))
        .catch((err) => {
            _examplesPromise = null;
            throw err;
        });
    return _examplesPromise;
}

function extractExamples(schema) {
    if (!schema) return [];
    const direct = schema.examples || schema.exampleQueries || schema.example_queries;
    if (Array.isArray(direct) && direct.length) return direct;
    // Walk the schema looking for objects with a `root` key — those
    // are query-spec shaped regardless of how the schema wraps them.
    const found = [];
    const seen = new Set();
    const visit = (node, depth = 0) => {
        if (!node || depth > 8) return;
        if (Array.isArray(node)) {
            for (const child of node) visit(child, depth + 1);
            return;
        }
        if (typeof node !== "object") return;
        if (
            node.root && typeof node.root === "object" &&
            (node.root.entity || node.root.alias)
        ) {
            const key = JSON.stringify(node);
            if (!seen.has(key)) {
                seen.add(key);
                found.push(node);
            }
            return; // don't descend into the spec itself
        }
        for (const value of Object.values(node)) visit(value, depth + 1);
    };
    visit(schema);
    return found;
}

// Defensive accessors — different schema versions have used slightly
// different top-level shapes (entities[], entityTypes[], etc.). We
// accept either.
function getEntities(schema) {
    return schema?.entities || schema?.entityTypes || schema?.types || [];
}
function getEdges(schema, entity) {
    return entity?.edges
        || entity?.relations
        || schema?.edges?.filter?.((edge) => sourceOf(edge) === nameOf(entity))
        || [];
}
function getFields(entity) {
    return entity?.fields || entity?.dimensions || [];
}
function getExamples(schema, entityName) {
    const all = schema?.examples || schema?.exampleQueries || [];
    if (!entityName) return all;
    return all.filter((ex) => JSON.stringify(ex).includes(entityName));
}

function nameOf(obj) {
    return obj?.name
        || obj?.entity
        || obj?.field
        || obj?.edge
        || obj?.metric
        || obj?.id
        || obj?.type
        || "";
}
function sourceOf(edge) {
    return edge?.source || edge?.from || "";
}
function targetOf(edge) {
    return edge?.target || edge?.to || "";
}
function descOf(obj) {
    return obj?.description || obj?.docstring || obj?.summary || "";
}

function asResult(payload) {
    return {
        content: [{ type: "text", text: JSON.stringify(payload) }],
        details: payload,
    };
}
function asError(msg) {
    return {
        content: [{ type: "text", text: msg }],
        details: { error: msg },
        isError: true,
    };
}

// schema_list_entities — orientation tool. Returns just names +
// one-line descriptions, NOT the field/edge bodies (those would
// blow the response size for what should be a cheap lookup).
export const listEntitiesTool = {
    name: "schema_list_entities",
    label: "List entities",
    description:
        "List every entity in the analytics schema with its one-line description. Cheap orientation tool — call this first when you don't know which entity to query. Returns { entities: [{ name, description }] }.",
    parameters: Type.Object({}),
    async execute() {
        let schema;
        try {
            schema = await describeSchema();
        } catch (err) {
            return asError(`schema fetch failed: ${err?.message || String(err)}`);
        }
        const entities = getEntities(schema).map((e) => ({
            name: nameOf(e),
            description: descOf(e),
        }));
        return asResult({ entities });
    },
};

// schema_describe_entity — the deep dive. Returns full field list,
// outgoing edges, and any example queries the schema ships that
// reference this entity. Heavier than list_entities but bounded to
// one entity, so still well under context limits.
export const describeEntityTool = {
    name: "schema_describe_entity",
    label: "Describe entity",
    description:
        "Return full details for one entity: all fields, edges to other entities, and example queries that reference it. Use after schema_list_entities to drill into a specific entity. Returns { name, description, fields, edges, examples }.",
    parameters: Type.Object({
        name: Type.String({
            description:
                "Exact entity name (use the casing from schema_list_entities, e.g. \"ENTITY_TYPE_PROMPT\").",
        }),
    }),
    async execute(_id, { name }) {
        let schema;
        try {
            schema = await describeSchema();
        } catch (err) {
            return asError(`schema fetch failed: ${err?.message || String(err)}`);
        }
        const target = String(name || "");
        const entity = getEntities(schema).find(
            (e) => nameOf(e) === target || nameOf(e)?.toLowerCase() === target.toLowerCase(),
        );
        if (!entity) {
            return asError(`no entity named "${target}"; call schema_list_entities to see valid names`);
        }
        return asResult({
            name: nameOf(entity),
            description: descOf(entity),
            fields: getFields(entity),
            edges: getEdges(schema, entity),
            examples: getExamples(schema, nameOf(entity)).slice(0, 6),
        });
    },
};

// schema_search — fuzzy lookup across entities, fields, edges, and
// examples. Use when you have a concept ("session id", "tool call")
// but don't know which entity owns it. Returns up to MAX_RESULTS
// matches ranked by how prominent the match was (name > description).
export const searchSchemaTool = {
    name: "schema_search",
    label: "Search schema",
    description:
        "Substring-search the schema for entities, fields, edges, or example queries matching `query`. Use when you know a concept (e.g. \"session id\", \"tool call\", \"endpoint\") but not which entity exposes it. Case-insensitive. Returns up to ~12 ranked matches with surrounding context.",
    parameters: Type.Object({
        query: Type.String({
            description: "Substring to look for. Splits on whitespace; all tokens must match.",
        }),
    }),
    async execute(_id, { query }) {
        let schema;
        try {
            schema = await describeSchema();
        } catch (err) {
            return asError(`schema fetch failed: ${err?.message || String(err)}`);
        }
        const tokens = String(query || "")
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);
        if (!tokens.length) return asResult({ matches: [] });

        const matches = [];
        const score = (haystack) => {
            const h = String(haystack || "").toLowerCase();
            if (!h) return 0;
            let n = 0;
            for (const t of tokens) {
                if (!h.includes(t)) return 0;
                n += 1;
            }
            return n;
        };

        for (const entity of getEntities(schema)) {
            const entName = nameOf(entity);
            const entDesc = descOf(entity);
            const entScore = score(`${entName} ${entDesc}`);
            if (entScore) {
                matches.push({
                    kind: "entity",
                    score: entScore * 3, // entity-level matches are most useful
                    path: entName,
                    name: entName,
                    description: entDesc,
                });
            }
            for (const field of getFields(entity)) {
                const fName = nameOf(field);
                const fDesc = descOf(field);
                const s = score(`${fName} ${fDesc}`);
                if (s) {
                    matches.push({
                        kind: "field",
                        score: s * 2,
                        path: `${entName}.${fName}`,
                        name: fName,
                        description: fDesc,
                        entity: entName,
                        type: field?.type || field?.valueType,
                    });
                }
            }
            for (const edge of getEdges(schema, entity)) {
                const eName = nameOf(edge);
                const from = sourceOf(edge) || entName;
                const to = targetOf(edge);
                const s = score(`${eName} ${descOf(edge)} ${from} ${to}`);
                if (s) {
                    matches.push({
                        kind: "edge",
                        score: s * 2,
                        path: `${entName}-[${eName}]->${to || "?"}`,
                        name: eName,
                        description: descOf(edge),
                        from,
                        to,
                    });
                }
            }
        }

        for (const example of getExamples(schema)) {
            const text = JSON.stringify(example);
            const s = score(text);
            if (s) {
                matches.push({
                    kind: "example",
                    score: s,
                    name: example?.name || example?.title || "example",
                    description: example?.description || "",
                    example,
                });
            }
        }

        matches.sort((a, b) => b.score - a.score);
        return asResult({ matches: matches.slice(0, MAX_RESULTS) });
    },
};

// Bundle for callers that want them all.
export const schemaTools = [listEntitiesTool, describeEntityTool, searchSchemaTool];
